import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, X, LayoutGrid, List, UserPlus, Pencil, Share2 } from "lucide-react";
import { apiFetch, apiPost, apiPatch } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

// As 5 etapas reais da jornada -- mesma ordem/rotulo do backend (conecta.js,
// STAGE_ORDER/STAGE_LABELS). E o que conta pro "Mover para..." (so faz
// sentido mover manualmente pra uma etapa real, gravada no status).
const PIPELINE_STAGES = [
  { id: "NEW", label: "Novo" },
  { id: "RETURNED", label: "Voltou" },
  { id: "APP_REGISTERED", label: "Cadastrou app" },
  { id: "RECURRING", label: "Frequentando" },
  { id: "INTEGRATED", label: "Integrado" }
];

// As 8 colunas do quadro Kanban: as 5 etapas reais mais 3 colunas calculadas
// (o backend manda em displayStatus/displayLabel -- ver attachEffectiveStage
// em conecta.js) pra quem sumiu depois de ja ter aparecido. isDerived marca
// as calculadas: não entram no avanço automático da jornada (PIPELINE_STAGES)
// nem no "status" gravado, mas aceitam marcação manual (arrastar ou "Mover
// para...") -- grava em manualStage no backend, pra quando o time já sabe
// que alguém sumiu antes do prazo padrão de 14/28 dias. Uma presença real
// registrada ("Compareceu") sempre desfaz a marcação manual.
const KANBAN_COLUMNS = [
  { id: "NEW", label: "Novo" },
  { id: "RETURNED", label: "Voltou" },
  { id: "APP_REGISTERED", label: "Cadastrou app" },
  { id: "RECURRING", label: "Frequentando" },
  { id: "AWAY", label: "Ausente", isDerived: true },
  { id: "INVITED_BACK", label: "Convidado", isDerived: true },
  { id: "NOT_INTEGRATED", label: "Não integrado", isDerived: true },
  { id: "INTEGRATED", label: "Integrado" }
];

const MANUAL_STAGE_OPTIONS = KANBAN_COLUMNS.filter((item) => item.isDerived);

// Modelos de mensagem pro WhatsApp contextual (os botoes de atalho aparecem
// conforme a etapa/status calculado do visitante selecionado, mais abaixo).
// Clicar so preenche a nota -- quem confere o texto e manda de fato e o
// botao "WhatsApp" já existente, que registra a interacao e abre o wa.me.
const WHATSAPP_TEMPLATES = {
  WELCOME: (visitor) => `Oi, ${visitor.name}! Aqui é da Igreja Batista Mosaico. Foi muito bom receber você. Queremos manter contato e te convidar para caminhar com a gente.`,
  DISCIPLESHIP_INVITE: (visitor) => `Oi, ${visitor.name}! Que alegria ter você com a gente de novo. Bora dar o próximo passo e participar de um grupo de discipulado? Me conta se topa.`,
  EVENT_INVITE: (visitor) => `Oi, ${visitor.name}! Sentimos sua falta por aqui. Temos um evento chegando e adoraríamos te ver de novo -- quer que eu te mande os detalhes?`,
  SERVICE_INVITE: (visitor) => `Oi, ${visitor.name}! Faz um tempo que não te vemos por aqui. Nosso culto está com as portas abertas pra você -- vem participar com a gente?`
};

// Estilo por tipo de entrada no Historico -- cada cor reaproveita a mesma cor
// da acao que gera aquela entrada (WhatsApp/Convite/Compareceu ja sao botoes
// coloridos assim mais abaixo), pra dar hierarquia visual e separar "mudanca
// de etapa" (sistema) das interacoes manuais.
const interactionKindStyle = {
  STAGE: { color: "#5EEAD4", label: "Etapa" },
  MANUAL_STAGE: { color: "#FCA5A5", label: "Manual" },
  WHATSAPP: { color: "#86EFAC", label: "WhatsApp" },
  INVITE: { color: "#FDE68A", label: "Convite" },
  ATTENDANCE: { color: "#BAE6FD", label: "Presença" },
  NOTE: { color: "#CBD5E1", label: "Anotação" }
};

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const fieldLabelStyle = { display: "block", margin: "0 0 7px", color: "#cbd5e1", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", fontWeight: 800 };

// Sem contraparte em AdminMembros/AdminCriancas/AdminEventos -- o quadro
// Kanban precisa de um container com scroll horizontal proprio (widthGuard/
// overflowGuard) e de botoes sem o estilo padrao do navegador, entao ficam
// definidos aqui do mesmo jeito que existiam em PrototypeApp.js.
const widthGuardStyle = { width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" };
const overflowGuardStyle = { ...widthGuardStyle, overflow: "hidden" };
const buttonReset = { background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", color: "inherit", padding: 0 };
const quickTemplateButtonStyle = { background: "none", cursor: "pointer", fontFamily: "inherit", padding: "6px 10px", fontSize: 10, fontWeight: 800, borderRadius: 999, border: "1px solid rgba(94,234,212,0.35)", backgroundColor: "rgba(20,184,166,0.10)", color: "#99F6E4" };

// Modal do "Novo visitante" -- mesmo padrao visual do modal de
// "Novo membro"/"Editar membro" em AdminMembros.jsx, pra manter os dois
// cadastros administrativos consistentes.
const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 680,
  margin: "3vh auto",
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

// Alternador Kanban/Lista -- pedido: o quadro (ou a lista) devia ser o topo
// da tela, nao o formulario de cadastro (que virou modal, aberto pelo botao
// "Novo visitante" ao lado, igual ja funciona em AdminMembros).
const viewToggleWrapStyle = {
  display: "inline-flex",
  padding: 3,
  borderRadius: 10,
  border: "1px solid rgba(148,163,184,0.24)",
  background: "rgba(255,255,255,0.03)",
  gap: 2
};
const viewToggleButtonStyle = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  minHeight: 32,
  padding: "0 12px",
  borderRadius: 8,
  border: "none",
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 900,
  cursor: "pointer",
  color: active ? "#0B1220" : "#9fb0cc",
  background: active ? "#5EEAD4" : "transparent"
});

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function AdminVisitantes() {
  const [status, setStatus] = useState("checking");
  const [viewerUser, setViewerUser] = useState(null);
  // Papeis do viewer (ver getEffectiveRoles) -- guardado a parte pra poder
  // calcular isReceptionOnly mais abaixo sem repetir a chamada.
  const [viewerRoles, setViewerRoles] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 390);

  const [conectaVisitors, setConectaVisitors] = useState([]);
  const [conectaLoading, setConectaLoading] = useState(false);
  const [conectaFeedback, setConectaFeedback] = useState("");
  const [draggedVisitorId, setDraggedVisitorId] = useState("");
  const [dragOverStage, setDragOverStage] = useState("");
  const [selectedVisitorId, setSelectedVisitorId] = useState("");
  // "" = cadastrando um visitante novo; id de um visitante existente =
  // editando os dados basicos dele (ver openEditVisitorModal).
  const [editingVisitorId, setEditingVisitorId] = useState("");
  // Filtro local (sobre os visitantes ja carregados) pra selecionar quem
  // aparece no Historico sem precisar ir ate o quadro Kanban.
  const [historyFilter, setHistoryFilter] = useState("");
  const [visitorForm, setVisitorForm] = useState({ name: "", whatsapp: "", email: "", city: "", neighborhood: "", source: "", status: "NEW", tags: "", notes: "", companions: [], markAsHouseholdContact: false });
  const [companionSearch, setCompanionSearch] = useState("");
  const [companionResults, setCompanionResults] = useState([]);
  const [companionSearchLoading, setCompanionSearchLoading] = useState(false);
  const [visitorInteractionForm, setVisitorInteractionForm] = useState({ type: "NOTE", title: "", note: "", eventName: "", attended: false });
  const [cascadeHouseholdOnAdvance, setCascadeHouseholdOnAdvance] = useState(false);
  const [showVisitorFormModal, setShowVisitorFormModal] = useState(false);
  // Feedback visual do botao "Compartilhar link" (formulario publico de
  // autocadastro) -- mesmo padrao do AdminMembros.jsx.
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const copyShareLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard?.writeText(url).then(() => {
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2500);
    });
  };
  // Modal de confirmacao pra converter o visitante selecionado em cadastro
  // de membro (so aparece quando ele esta em "Integrado") -- mesmo padrao
  // de convertTarget/confirmConvert em AdminCriancas.jsx.
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertingVisitorId, setConvertingVisitorId] = useState("");
  // Lembra a ultima visualizacao escolhida (Kanban ou Lista) entre visitas
  // a pagina -- por navegador, nao sincronizado entre pessoas da equipe.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return window.localStorage.getItem("mosaicoVisitantesView") === "lista" ? "lista" : "kanban";
    } catch (error) {
      return "kanban";
    }
  });
  const [listFilter, setListFilter] = useState("");

  useEffect(() => {
    setCascadeHouseholdOnAdvance(false);
  }, [selectedVisitorId]);

  useEffect(() => {
    try {
      window.localStorage.setItem("mosaicoVisitantesView", viewMode);
    } catch (error) {
      // localStorage indisponivel (modo privado etc.) -- so nao persiste.
    }
  }, [viewMode]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        // Liberado tambem pra SECRETARIA e ACOLHIMENTO (pedido: "dar acesso de
        // visitantes para perfil secretaria, e para perfil acolhimento") --
        // mesmo gate aplicado no card do painel (AdminHome.jsx) e no
        // backend (requireVisitorAccess em conecta.js). RECEPCAO tambem
        // passa aqui, mas so enxerga a view restrita mais abaixo
        // (isReceptionOnly) -- o gate real de verdade pra ela e o backend
        // (requireVisitorReceptionAccess em conecta.js).
        const roles = getEffectiveRoles(me);
        const canAccess = roles.includes("ADMIN") || roles.includes("SECRETARIA") || roles.includes("ACOLHIMENTO") || roles.includes("RECEPCAO");
        if (canAccess && !me.passwordMustChange) {
          setViewerUser(me);
          setViewerRoles(roles);
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const isDesktop = viewportWidth >= 1080;
  const isTablet = viewportWidth >= 768;
  const isMobile = !isTablet;
  const inputStyle = {
    width: "100%",
    border: "1px solid rgba(226,232,240,0.16)",
    background: "rgba(12,20,34,0.68)",
    color: "#F8FAFC",
    borderRadius: 14,
    padding: "12px 13px",
    fontSize: isMobile ? 16 : 14,
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)"
  };

  const canManage = status === "ready" && Boolean(viewerUser?.id);
  // RECEPCAO sozinho (sem ADMIN/SECRETARIA/ACOLHIMENTO tambem) so ve a view
  // restrita mais abaixo -- cadastrar visitante novo + busca de duplicidade.
  // Camada de UX; o gate que importa de verdade e o backend
  // (requireVisitorReceptionAccess em conecta.js).
  const isReceptionOnly = viewerRoles.includes("RECEPCAO")
    && !viewerRoles.includes("ADMIN")
    && !viewerRoles.includes("SECRETARIA")
    && !viewerRoles.includes("ACOLHIMENTO");
  const selectedVisitor = conectaVisitors.find((visitor) => visitor.id === selectedVisitorId) || conectaVisitors[0] || null;
  const historyFilterTerm = historyFilter.trim().toLowerCase();
  const historyFilteredVisitors = historyFilterTerm
    ? conectaVisitors.filter((visitor) => (
        (visitor.name || "").toLowerCase().includes(historyFilterTerm) ||
        (visitor.whatsapp || "").toLowerCase().includes(historyFilterTerm) ||
        (visitor.city || "").toLowerCase().includes(historyFilterTerm)
      ))
    : conectaVisitors;
  const listFilterTerm = listFilter.trim().toLowerCase();
  const listFilteredVisitors = listFilterTerm
    ? conectaVisitors.filter((visitor) => (
        (visitor.name || "").toLowerCase().includes(listFilterTerm) ||
        (visitor.whatsapp || "").toLowerCase().includes(listFilterTerm) ||
        (visitor.city || "").toLowerCase().includes(listFilterTerm)
      ))
    : conectaVisitors;

  const loadConectaVisitors = async () => {
    if (!canManage) return;
    setConectaLoading(true);
    try {
      // Sem campo de busca proprio aqui -- essa tela sempre carrega os
      // visitantes mais recentes (limite do backend); selecionar/filtrar
      // quem ja esta na tela acontece no filtro ao lado do Historico.
      const params = new URLSearchParams({ viewerUserId: viewerUser.id });
      const data = await apiFetch(`/conecta/visitors?${params.toString()}`, { method: "GET", viewer: viewerUser });
      setConectaVisitors(data);
      if (!selectedVisitorId && data[0]?.id) setSelectedVisitorId(data[0].id);
    } catch (error) {
      setConectaFeedback("Não foi possível carregar o Conecta agora.");
    } finally {
      setConectaLoading(false);
    }
  };

  useEffect(() => {
    // RECEPCAO nao tem acesso a GET /conecta/visitors no backend
    // (requireVisitorReceptionAccess so libera companions/create) -- nao
    // tem por que nem tentar carregar a lista completa pra ela.
    if (canManage && !isReceptionOnly) loadConectaVisitors();
  }, [canManage, isReceptionOnly]);

  // Deep-link vindo da aba "Partilhar" de uma trilha (mesa): admin clicou
  // em "Ver cadastro" no comentario de um visitante e caiu aqui com
  // ?personId=<Visitor.id> -- so' seleciona (mesma acao do clique no nome,
  // linha da lista). Como esta tela carrega so' os visitantes mais
  // recentes (ver loadConectaVisitors acima), um visitante bem antigo pode
  // nao estar na lista ainda -- nesse caso o link simplesmente nao
  // seleciona nada, sem erro.
  const [searchParams] = useSearchParams();
  const appliedPersonDeepLinkRef = useRef(false);
  useEffect(() => {
    if (appliedPersonDeepLinkRef.current) return;
    const targetId = searchParams.get("personId");
    if (!targetId || !conectaVisitors.length) return;
    const match = conectaVisitors.find((visitor) => visitor.id === targetId);
    if (match) {
      appliedPersonDeepLinkRef.current = true;
      setSelectedVisitorId(match.id);
    }
  }, [conectaVisitors, searchParams]);

  const updateVisitorForm = (field, value) => {
    setVisitorForm((prev) => ({ ...prev, [field]: value }));
    if (conectaFeedback) setConectaFeedback("");
  };

  const updateVisitorInteractionForm = (field, value) => {
    setVisitorInteractionForm((prev) => ({ ...prev, [field]: value }));
    if (conectaFeedback) setConectaFeedback("");
  };

  const searchConectaCompanions = async (query) => {
    setCompanionSearch(query);
    if (!canManage) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setCompanionResults([]);
      return;
    }
    setCompanionSearchLoading(true);
    try {
      // Endpoint unificado -- devolve visitante ja cadastrado e membro da
      // igreja juntos (cada item marcado com "type"), pra "Veio com" poder
      // apontar pra qualquer um dos dois.
      const params = new URLSearchParams({ viewerUserId: viewerUser.id, search: trimmed, limit: "6" });
      const data = await apiFetch(`/conecta/companions?${params.toString()}`, { method: "GET", viewer: viewerUser });
      setCompanionResults(data.filter((item) => !visitorForm.companions.some((selected) => selected.id === item.id && selected.type === item.type)));
    } catch (error) {
      setCompanionResults([]);
    } finally {
      setCompanionSearchLoading(false);
    }
  };

  const addCompanion = (item) => {
    setVisitorForm((prev) => prev.companions.some((selected) => selected.id === item.id && selected.type === item.type)
      ? prev
      : { ...prev, companions: [...prev.companions, { id: item.id, name: item.name, type: item.type }] });
    setCompanionSearch("");
    setCompanionResults([]);
  };

  const removeCompanion = (item) => {
    setVisitorForm((prev) => ({ ...prev, companions: prev.companions.filter((selected) => !(selected.id === item.id && selected.type === item.type)) }));
  };

  const saveConectaVisitor = async () => {
    if (!canManage) return;
    if (!visitorForm.name.trim() || !visitorForm.whatsapp.replace(/\D/g, "")) {
      setConectaFeedback("Informe nome e WhatsApp do visitante.");
      return;
    }
    setConectaLoading(true);
    setConectaFeedback("");
    try {
      if (editingVisitorId) {
        // Edicao de dados basicos de um visitante ja cadastrado -- so os
        // campos deste formulario. Nao manda status/companionIds/
        // companionMemberIds/isHouseholdContact aqui de proposito: a rota
        // PATCH generica (conecta.js) so toca em status/household quando
        // esses campos vem no corpo, e companionMemberIds em especial seria
        // sobrescrito pra [] se mandassemos a lista vazia do formulario,
        // apagando um vinculo que o visitante ja tinha.
        const updated = await apiPatch(`/conecta/visitors/${encodeURIComponent(editingVisitorId)}`, {
          viewer: viewerUser,
          body: {
            viewerUserId: viewerUser.id,
            name: visitorForm.name.trim(),
            whatsapp: visitorForm.whatsapp.trim(),
            email: visitorForm.email.trim() || null,
            city: visitorForm.city.trim() || null,
            neighborhood: visitorForm.neighborhood.trim() || null,
            source: visitorForm.source.trim() || null,
            tags: visitorForm.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
            notes: visitorForm.notes.trim() || null
          }
        });
        setConectaVisitors((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
        setSelectedVisitorId(updated.id);
        setConectaFeedback("Dados do visitante atualizados.");
      } else {
        const visitor = await apiPost("/conecta/visitors", {
          viewer: viewerUser,
          body: {
            viewerUserId: viewerUser.id,
            name: visitorForm.name.trim(),
            whatsapp: visitorForm.whatsapp.trim(),
            email: visitorForm.email.trim() || null,
            city: visitorForm.city.trim() || null,
            neighborhood: visitorForm.neighborhood.trim() || null,
            source: visitorForm.source.trim() || null,
            status: visitorForm.status,
            tags: visitorForm.tags.split(",").map((tag) => tag.trim()).filter(Boolean),
            notes: visitorForm.notes.trim() || null,
            // "Veio com" pode misturar visitante (entra no household, ver
            // resolveHousehold no backend) e membro da igreja (so informativo,
            // companionMemberIds) -- separa os dois aqui antes de mandar.
            companionIds: visitorForm.companions.filter((item) => item.type === "visitor").map((item) => item.id),
            companionMemberIds: visitorForm.companions.filter((item) => item.type === "member").map((item) => item.id),
            isHouseholdContact: visitorForm.markAsHouseholdContact
          }
        });
        setConectaVisitors((prev) => [visitor, ...prev]);
        setSelectedVisitorId(visitor.id);
        setConectaFeedback("Visitante salvo no Conecta.");
      }
      setVisitorForm({ name: "", whatsapp: "", email: "", city: "", neighborhood: "", source: "", status: "NEW", tags: "", notes: "", companions: [], markAsHouseholdContact: false });
      setCompanionSearch("");
      setCompanionResults([]);
      setEditingVisitorId("");
      setShowVisitorFormModal(false);
    } catch (error) {
      setConectaFeedback(editingVisitorId ? "Não foi possível atualizar esse visitante agora." : "Não foi possível salvar esse visitante agora.");
    } finally {
      setConectaLoading(false);
    }
  };

  const closeVisitorFormModal = () => {
    if (conectaLoading) return;
    setShowVisitorFormModal(false);
    setEditingVisitorId("");
  };

  // Abre o mesmo modal do "Novo visitante", mas pre-preenchido com os dados
  // atuais do visitante selecionado e marcado pra editar (editingVisitorId).
  // Deixa de fora etapa/"veio com" de proposito -- isso continua so pelo
  // "Mover para..."/"Avancar etapa" e pela busca de companheiro, pra nao
  // mexer em household/status sem querer a partir de uma edicao de dados
  // basicos (ver saveConectaVisitor).
  const openEditVisitorModal = (visitor) => {
    if (!visitor) return;
    setEditingVisitorId(visitor.id);
    setVisitorForm({
      name: visitor.name || "",
      whatsapp: visitor.whatsapp || "",
      email: visitor.email || "",
      city: visitor.city || "",
      neighborhood: visitor.neighborhood || "",
      source: visitor.source || "",
      status: visitor.status || "NEW",
      tags: (visitor.tags || []).join(", "),
      notes: visitor.notes || "",
      companions: [],
      markAsHouseholdContact: false
    });
    setCompanionSearch("");
    setCompanionResults([]);
    setShowVisitorFormModal(true);
  };

  const addVisitorInteraction = async (typeOverride) => {
    if (!selectedVisitor || !canManage) return null;
    const type = typeOverride || visitorInteractionForm.type;
    const title = visitorInteractionForm.title.trim() || (
      type === "WHATSAPP" ? "Mensagem enviada pelo WhatsApp" :
      type === "INVITE" ? "Convite enviado" :
      type === "ATTENDANCE" ? "Presença registrada" :
      "Anotação"
    );
    try {
      const visitor = await apiPost(`/conecta/visitors/${encodeURIComponent(selectedVisitor.id)}/interactions`, {
        viewer: viewerUser,
        body: {
          viewerUserId: viewerUser.id,
          type,
          title,
          note: visitorInteractionForm.note.trim() || null,
          eventName: visitorInteractionForm.eventName.trim() || null,
          attended: type === "ATTENDANCE" ? true : null
        }
      });
      setConectaVisitors((prev) => prev.map((item) => item.id === visitor.id ? visitor : item));
      setVisitorInteractionForm({ type: "NOTE", title: "", note: "", eventName: "", attended: false });
      setConectaFeedback("Histórico atualizado.");
      return visitor;
    } catch (error) {
      setConectaFeedback("Não foi possível registrar essa interação.");
      return null;
    }
  };

  // Move o visitante pra uma etapa qualquer da jornada -- usado pelo arrastar-
  // e-soltar do quadro Kanban e pelo seletor "mover para" no detalhe. Ao
  // contrario de advanceVisitorStage, aceita qualquer etapa de destino
  // (inclusive pra tras), nao so a proxima da sequencia. cascadeHousehold,
  // quando true, aplica a mesma etapa pra toda a familia/grupo vinculado de
  // uma vez.
  const moveVisitorToStage = async (visitor, newStatus, cascadeHousehold) => {
    if (!visitor || !newStatus || newStatus === visitor.status) return;
    if (!canManage) return;
    try {
      const updated = await apiPatch(`/conecta/visitors/${encodeURIComponent(visitor.id)}/status`, {
        viewer: viewerUser,
        body: { viewerUserId: viewerUser.id, status: newStatus, cascadeHousehold: !!cascadeHousehold }
      });
      if (cascadeHousehold) {
        await loadConectaVisitors();
      } else {
        setConectaVisitors((prev) => prev.map((item) => item.id === updated.id ? updated : item));
      }
      setCascadeHouseholdOnAdvance(false);
      setConectaFeedback("Etapa atualizada.");
    } catch (error) {
      setConectaFeedback("Não foi possível mover o visitante agora.");
    }
  };

  // Converte o visitante selecionado (ja em "Integrado") num cadastro de
  // membro -- cria o Member com o que ja temos (nome, whatsapp, cidade/
  // bairro) e so marca o vinculo (convertedMemberId) no visitante, sem
  // apagar o historico de interacoes/jornada. Mesmo padrao de
  // confirmConvert em AdminCriancas.jsx.
  const confirmConvertVisitor = async () => {
    const visitor = selectedVisitor;
    if (!visitor || !canManage) return;
    setConvertingVisitorId(visitor.id);
    setConectaFeedback("");
    try {
      const { member } = await apiPost(`/conecta/visitors/${encodeURIComponent(visitor.id)}/convert-to-member`, {
        viewer: viewerUser,
        body: { viewerUserId: viewerUser.id }
      });
      setConectaVisitors((prev) =>
        prev.map((item) =>
          item.id === visitor.id ? { ...item, convertedMemberId: member.id, convertedMember: member } : item
        )
      );
      setShowConvertModal(false);
      setConectaFeedback(`${visitor.name} agora está no cadastro de membros.`);
    } catch (error) {
      setConectaFeedback(error?.message || "Não foi possível converter esse visitante em membro.");
      setShowConvertModal(false);
    } finally {
      setConvertingVisitorId("");
    }
  };

  // Avanca a etapa da jornada de acompanhamento pro proximo estagio real da
  // sequencia (PIPELINE_STAGES). cascadeHousehold, quando true, aplica a
  // mesma etapa pra toda a familia/grupo vinculado de uma vez.
  const advanceVisitorStage = (visitor, cascadeHousehold) => {
    if (!visitor) return;
    const stageIds = PIPELINE_STAGES.map((item) => item.id);
    const currentIndex = stageIds.indexOf(visitor.status);
    const nextStatus = stageIds[currentIndex + 1];
    if (!nextStatus) return;
    return moveVisitorToStage(visitor, nextStatus, cascadeHousehold);
  };

  const sendVisitorWhatsapp = async (templateMessage) => {
    if (!selectedVisitor) return;
    const message = templateMessage || visitorInteractionForm.note.trim() || WHATSAPP_TEMPLATES.WELCOME(selectedVisitor);
    await addVisitorInteraction("WHATSAPP");
    window.open(`https://wa.me/55${selectedVisitor.whatsapp.replace(/^55/, "")}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à equipe administrativa. Entre com uma conta autorizada para acessar os visitantes.</CenteredMessage>;
  }

  // View restrita pro papel RECEPCAO (voluntario de recepcao presencial no
  // culto): so o formulario de "Novo visitante" + busca de duplicidade em
  // "Veio com". Sem quadro Kanban, lista, historico ou conversao em membro
  // -- essas ficam so pra ADMIN/SECRETARIA/ACOLHIMENTO (canManage completo
  // mais abaixo). O gate que importa de verdade e o backend
  // (requireVisitorReceptionAccess em conecta.js): isso aqui e so a tela.
  if (isReceptionOnly) {
    return (
      <div style={pageStyle}>
        <div style={{ maxWidth: 560, margin: "0 auto" }}>
          <Link to={ADMIN_PATH} style={backLinkStyle}>
            <ArrowLeft size={14} /> Voltar
          </Link>
          <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Recepção</p>
          <h1 style={{ margin: "0 0 6px", fontSize: 24, fontWeight: 950, color: "#FFF" }}>Novo visitante</h1>
          <p style={{ margin: "0 0 20px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.5 }}>
            Cadastre quem chegou agora no culto. Se a pessoa já visitou antes, busque pelo nome em "Veio com" pra não duplicar o cadastro.
          </p>
          <div style={{ display: "grid", gap: 9 }}>
            <label>
              <span style={fieldLabelStyle}>Nome <span style={{ color: "#FCA5A5" }}>*</span></span>
              <input value={visitorForm.name} onChange={(event) => updateVisitorForm("name", event.target.value)} placeholder="Nome do visitante" style={inputStyle} />
            </label>
            <label>
              <span style={fieldLabelStyle}>WhatsApp <span style={{ color: "#FCA5A5" }}>*</span></span>
              <input value={visitorForm.whatsapp} onChange={(event) => updateVisitorForm("whatsapp", event.target.value)} placeholder="WhatsApp" inputMode="tel" style={inputStyle} />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 9 }}>
              <label>
                <span style={fieldLabelStyle}>Cidade</span>
                <input value={visitorForm.city} onChange={(event) => updateVisitorForm("city", event.target.value)} placeholder="Cidade" style={inputStyle} />
              </label>
              <label>
                <span style={fieldLabelStyle}>Bairro</span>
                <input value={visitorForm.neighborhood} onChange={(event) => updateVisitorForm("neighborhood", event.target.value)} placeholder="Bairro" style={inputStyle} />
              </label>
            </div>
            <label>
              <span style={fieldLabelStyle}>Como chegou</span>
              <input value={visitorForm.source} onChange={(event) => updateVisitorForm("source", event.target.value)} placeholder="Convite de um amigo, redes sociais..." style={inputStyle} />
            </label>
            <div style={{ position: "relative" }}>
              <span style={fieldLabelStyle}>Veio com (visitante, família ou membro da igreja)</span>
              <input value={companionSearch} onChange={(event) => searchConectaCompanions(event.target.value)} placeholder="Buscar visitante ou membro já cadastrado..." style={inputStyle} />
              {companionResults.length > 0 && (
                <div style={{ position: "absolute", zIndex: 5, top: "100%", left: 0, right: 0, marginTop: 4, border: "1px solid rgba(94,234,212,0.34)", background: "#0F172A", maxHeight: 180, overflowY: "auto" }}>
                  {companionResults.map((item) => (
                    <button key={item.type + item.id} onClick={() => addCompanion(item)} style={{ ...buttonReset, width: "100%", textAlign: "left", padding: "9px 11px", color: "#E5E7EB", fontSize: 12, borderBottom: "1px solid rgba(148,163,184,0.14)" }}>
                      <strong>{item.name}</strong>
                      <span style={{ marginLeft: 6, color: "#9fb0cc", fontSize: 11 }}>{item.subtitle}</span>
                      <span style={{ marginLeft: 6, color: item.type === "member" ? "#99F6E4" : "#9fb0cc", fontSize: 10, fontWeight: 800, textTransform: "uppercase" }}>
                        {item.type === "member" ? "Membro" : "Visitante"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {visitorForm.companions.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {visitorForm.companions.map((item) => (
                  <span key={item.type + item.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 9px", border: "1px solid rgba(94,234,212,0.34)", background: "rgba(20,184,166,0.12)", color: "#99F6E4", fontSize: 11, fontWeight: 800 }}>
                    {item.name}
                    {item.type === "member" && <span style={{ fontSize: 9, fontWeight: 900, opacity: 0.8 }}>(membro)</span>}
                    <button onClick={() => removeCompanion(item)} style={{ ...buttonReset, color: "#99F6E4", fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
            {visitorForm.companions.some((item) => item.type === "visitor") && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#CBD5E1" }}>
                <input type="checkbox" checked={visitorForm.markAsHouseholdContact} onChange={(event) => updateVisitorForm("markAsHouseholdContact", event.target.checked)} />
                Este é o responsável do grupo (recebe o acompanhamento)
              </label>
            )}
            <button onClick={saveConectaVisitor} disabled={conectaLoading} style={{ ...buttonReset, minHeight: 46, border: "1px solid rgba(94,234,212,0.45)", background: "rgba(20,184,166,0.16)", color: "#99F6E4", fontSize: 12, fontWeight: 950, opacity: conectaLoading ? 0.7 : 1 }}>
              {conectaLoading ? "Salvando..." : "Salvar visitante"}
            </button>
            {conectaFeedback && <p style={{ margin: 0, color: conectaFeedback.includes("Não") || conectaFeedback.includes("Informe") ? "#FCA5A5" : "#A7F3D0", fontSize: 12 }}>{conectaFeedback}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Área administrativa</p>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Visitantes</h1>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div style={viewToggleWrapStyle}>
              <button type="button" onClick={() => setViewMode("kanban")} style={viewToggleButtonStyle(viewMode === "kanban")}>
                <LayoutGrid size={13} /> Kanban
              </button>
              <button type="button" onClick={() => setViewMode("lista")} style={viewToggleButtonStyle(viewMode === "lista")}>
                <List size={13} /> Lista
              </button>
            </div>
            <button
              type="button"
              onClick={() => copyShareLink("/cadastro/visitante")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.12)", color: "#FDE68A", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
              title="Copia o link do formulário público de cadastro de visitante"
            >
              <Share2 size={13} />
              {shareLinkCopied ? "Link copiado!" : "Compartilhar link"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditingVisitorId("");
                setVisitorForm({ name: "", whatsapp: "", email: "", city: "", neighborhood: "", source: "", status: "NEW", tags: "", notes: "", companions: [], markAsHouseholdContact: false });
                setCompanionSearch("");
                setCompanionResults([]);
                setShowVisitorFormModal(true);
              }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.16)", color: "#86EFAC", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
            >
              <Plus size={14} />
              Novo visitante
            </button>
          </div>
        </div>
        <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 640 }}>
          Cadastre quem chegou, registre contatos, envie uma mensagem pelo WhatsApp e acompanhe se a pessoa voltou, respondeu ou precisa de cuidado. Use "Compartilhar link" pra mandar o formulário público pra quem acabou de visitar -- o cadastro já entra na etapa "Novo".
        </p>

        {viewMode === "lista" && (
        <div style={{ ...cardStyle, ...overflowGuardStyle, padding: isMobile ? 14 : 16, marginTop: 0, marginBottom: 0, borderRadius: isDesktop ? 16 : 12 }}>
          <p style={{ margin: "0 0 10px", color: "#5EEAD4", fontSize: 11, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>Lista de visitantes</p>
          <input
            value={listFilter}
            onChange={(event) => setListFilter(event.target.value)}
            placeholder="Buscar por nome, WhatsApp ou cidade..."
            style={{ ...inputStyle, marginBottom: 10 }}
          />
          <div style={{ display: "grid", gap: 6, maxHeight: isDesktop ? 560 : 420, overflowY: "auto", paddingRight: 4 }}>
            {listFilteredVisitors.map((visitor) => {
              const isSelected = selectedVisitor?.id === visitor.id;
              const stageInfo = KANBAN_COLUMNS.find((item) => item.id === (visitor.displayStatus || visitor.status));
              return (
                <button
                  key={visitor.id}
                  type="button"
                  onClick={() => setSelectedVisitorId(visitor.id)}
                  style={{
                    ...buttonReset,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    flexWrap: "wrap",
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: isSelected ? "1px solid rgba(94,234,212,0.58)" : "1px solid rgba(148,163,184,0.16)",
                    background: isSelected ? "rgba(20,184,166,0.12)" : "rgba(255,255,255,0.035)"
                  }}
                >
                  <span style={{ minWidth: 0 }}>
                    <strong style={{ display: "block", fontSize: 13, color: "#E5E7EB", overflowWrap: "anywhere" }}>{visitor.name}</strong>
                    <span style={{ display: "block", color: "#9fb0cc", fontSize: 11, marginTop: 2, overflowWrap: "anywhere" }}>
                      {visitor.whatsapp} {visitor.city ? `• ${visitor.city}` : ""}
                    </span>
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    {visitor.displayStatus && visitor.displayStatus !== visitor.status && (
                      <span style={{ color: "#FCA5A5", fontSize: 9, fontWeight: 900 }}>⚠ {visitor.displayLabel}{visitor.manual ? " · manual" : ` · ${visitor.daysSinceVisit}d`}</span>
                    )}
                    <span style={{ padding: "4px 9px", borderRadius: 999, fontSize: 10, fontWeight: 900, color: "#5EEAD4", border: "1px solid rgba(94,234,212,0.35)", background: "rgba(20,184,166,0.10)", whiteSpace: "nowrap" }}>
                      {stageInfo?.label || visitor.status}
                    </span>
                  </span>
                </button>
              );
            })}
            {!listFilteredVisitors.length && <p style={{ margin: 0, color: "#64748B", fontSize: 12, textAlign: "center", padding: "20px 0" }}>Nenhum visitante encontrado.</p>}
          </div>
        </div>
        )}

        {viewMode === "kanban" && (
        <div style={{ ...cardStyle, ...overflowGuardStyle, padding: isMobile ? 14 : 16, marginTop: 14, marginBottom: 0, borderRadius: isDesktop ? 16 : 12 }}>
          <p style={{ margin: "0 0 6px", color: "#5EEAD4", fontSize: 11, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>Quadro de acompanhamento</p>
          <p style={{ margin: "0 0 10px", color: "#9fb0cc", fontSize: 11, lineHeight: 1.5 }}>Arraste um cartão entre as colunas (ou use o "Mover para..." no próprio cartão, no celular) pra mudar a etapa. Clique no cartão pra abrir o histórico.</p>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 8, WebkitOverflowScrolling: "touch" }}>
            {KANBAN_COLUMNS.map((stage) => {
              // Dois casos saem do quadro de acompanhamento ativo (continuam
              // existindo e aparecem normal na visao "Lista", so nao ficam
              // num board pensado pra contato ativo):
              // 1) quem ja virou membro (convertedMemberId preenchido) --
              //    a jornada de visitante terminou, nao tem o que acompanhar.
              // 2) quem veio de "Transformar em visitante" por ter SAIDO de
              //    ser membro (source do convert-to-visitor em members.js) --
              //    diferente de "Ausente" (que e pra gente tentar reconquistar
              //    quem sumiu sem avisar), aqui a pessoa ja decidiu, entao nao
              //    faz sentido ficar pedindo contato ativo com ela.
              const isOutOfBoard = (visitor) => Boolean(visitor.convertedMemberId) || visitor.source === "Convertido de cadastro de membro";
              const stageVisitors = conectaVisitors.filter((visitor) => !isOutOfBoard(visitor) && (visitor.displayStatus || visitor.status) === stage.id);
              const isDragOver = dragOverStage === stage.id;
              return (
                <div
                  key={stage.id}
                  onDragOver={(event) => { event.preventDefault(); if (draggedVisitorId) setDragOverStage(stage.id); }}
                  onDragLeave={() => setDragOverStage((prev) => (prev === stage.id ? "" : prev))}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragOverStage("");
                    setDraggedVisitorId("");
                    const visitorId = event.dataTransfer.getData("text/plain") || draggedVisitorId;
                    const visitor = conectaVisitors.find((item) => item.id === visitorId);
                    if (visitor && (visitor.displayStatus || visitor.status) !== stage.id) moveVisitorToStage(visitor, stage.id, false);
                  }}
                  style={{ flex: "0 0 236px", minWidth: 236, maxWidth: 236, display: "flex", flexDirection: "column", borderRadius: 10, padding: 8, border: isDragOver ? "1px dashed rgba(94,234,212,0.68)" : "1px solid rgba(148,163,184,0.18)", background: isDragOver ? "rgba(20,184,166,0.10)" : stage.isDerived ? "rgba(148,163,184,0.045)" : "rgba(255,255,255,0.02)" }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 4px 8px", marginBottom: 8, borderBottom: "1px solid rgba(148,163,184,0.16)" }}>
                    <span style={{ color: "#CBD5E1", fontSize: 11, fontWeight: 900 }}>{stage.label}</span>
                    <span style={{ color: "#5EEAD4", fontSize: 11, fontWeight: 950 }}>{stageVisitors.length}</span>
                  </div>
                  <div style={{ display: "grid", gap: 7, minHeight: 60, maxHeight: isDesktop ? 480 : 340, overflowY: "auto", paddingRight: 8 }}>
                    {stageVisitors.map((visitor) => (
                      <div
                        key={visitor.id}
                        draggable
                        onDragStart={(event) => { event.dataTransfer.setData("text/plain", visitor.id); event.dataTransfer.effectAllowed = "move"; setDraggedVisitorId(visitor.id); }}
                        onDragEnd={() => { setDraggedVisitorId(""); setDragOverStage(""); }}
                        onClick={() => setSelectedVisitorId(visitor.id)}
                        style={{ cursor: "grab", boxSizing: "border-box", padding: 10, border: selectedVisitor?.id === visitor.id ? "1px solid rgba(94,234,212,0.58)" : "1px solid rgba(148,163,184,0.16)", background: selectedVisitor?.id === visitor.id ? "rgba(20,184,166,0.12)" : "rgba(255,255,255,0.035)", opacity: draggedVisitorId === visitor.id ? 0.4 : 1 }}
                      >
                        <strong style={{ display: "block", fontSize: 12, color: "#E5E7EB", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{visitor.name}</strong>
                        <p style={{ margin: "4px 0 0", color: "#9fb0cc", fontSize: 10, overflowWrap: "anywhere" }}>{visitor.whatsapp} {visitor.city ? `• ${visitor.city}` : ""}</p>
                        {visitor.displayStatus && visitor.displayStatus !== visitor.status && (
                          <p style={{ margin: "4px 0 0", color: "#FCA5A5", fontSize: 9, fontWeight: 900 }}>
                            ⚠ {visitor.displayLabel}{visitor.manual ? " · marcado manualmente" : ` · ${visitor.daysSinceVisit}d sem presença`}
                          </p>
                        )}
                        {visitor.householdMembers?.length > 0 && (
                          <p style={{ margin: "4px 0 0", color: "#7DD3FC", fontSize: 9, fontWeight: 800, overflowWrap: "anywhere" }}>
                            {visitor.isHouseholdContact ? "★ " : ""}Família · {visitor.householdMembers.length + 1}
                          </p>
                        )}
                        {/* Fallback pra quem está no celular: drag-and-drop HTML5 não
                            funciona em touch, então mover etapa não pode depender só dele. */}
                        <select
                          value=""
                          onClick={(event) => event.stopPropagation()}
                          onChange={(event) => { event.stopPropagation(); if (event.target.value) moveVisitorToStage(visitor, event.target.value, false); }}
                          style={{ ...inputStyle, marginTop: 7, minHeight: 34, padding: "0 6px", fontSize: 10 }}
                        >
                          <option value="">Mover para...</option>
                          {PIPELINE_STAGES.filter((item) => item.id !== visitor.status).map((item) => (
                            <option key={item.id} value={item.id}>{item.label}</option>
                          ))}
                          {MANUAL_STAGE_OPTIONS.filter((item) => item.id !== visitor.displayStatus).map((item) => (
                            <option key={item.id} value={item.id}>{item.label} (manual)</option>
                          ))}
                          {visitor.manual && <option value="AUTO">Voltar ao automático</option>}
                        </select>
                      </div>
                    ))}
                    {!stageVisitors.length && <p style={{ margin: 0, color: "#64748B", fontSize: 10, textAlign: "center", padding: "14px 0" }}>Vazio</p>}
                  </div>
                </div>
              );
            })}
          </div>
          {!conectaVisitors.length && <p style={{ margin: "10px 0 0", color: "#9fb0cc", fontSize: 12 }}>Nenhum visitante encontrado.</p>}
        </div>
        )}

        <div style={{ ...cardStyle, ...overflowGuardStyle, padding: isMobile ? 14 : 16, marginTop: 14, borderRadius: isDesktop ? 16 : 12, borderColor: "rgba(94,234,212,0.25)" }}>
          {conectaVisitors.length > 0 && (
            // Busca logo acima do Historico + listagem ja visivel dos
            // visitantes carregados -- filtra ao digitar, clica pra
            // selecionar. Evita depender do quadro Kanban la em cima so
            // pra trocar de pessoa.
            <div style={{ marginBottom: 14 }}>
              <span style={fieldLabelStyle}>Buscar</span>
              <input
                value={historyFilter}
                onChange={(event) => setHistoryFilter(event.target.value)}
                placeholder="Nome, WhatsApp ou cidade..."
                style={inputStyle}
              />
              <div style={{ display: "flex", gap: 6, overflowX: "auto", marginTop: 8, paddingBottom: 2, WebkitOverflowScrolling: "touch" }}>
                {historyFilteredVisitors.map((visitor) => {
                  const isSelected = selectedVisitor?.id === visitor.id;
                  const stageLabel = visitor.displayLabel || visitor.status;
                  return (
                    <button
                      key={visitor.id}
                      onClick={() => setSelectedVisitorId(visitor.id)}
                      style={{
                        ...buttonReset,
                        flex: "0 0 auto",
                        padding: "7px 11px",
                        border: isSelected ? "1px solid rgba(94,234,212,0.68)" : "1px solid rgba(148,163,184,0.22)",
                        background: isSelected ? "rgba(20,184,166,0.18)" : "rgba(255,255,255,0.035)",
                        color: isSelected ? "#99F6E4" : "#E5E7EB",
                        fontSize: 11,
                        fontWeight: 800,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {visitor.name}
                      <span style={{ marginLeft: 6, color: isSelected ? "#5EEAD4" : "#64748B", fontWeight: 700 }}>{stageLabel}</span>
                    </button>
                  );
                })}
                {!historyFilteredVisitors.length && <p style={{ margin: 0, color: "#64748B", fontSize: 11 }}>Nenhum visitante encontrado.</p>}
              </div>
            </div>
          )}
          {selectedVisitor ? (
            <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 0.85fr) minmax(0, 1.15fr)" : "minmax(0, 1fr)", gap: 14 }}>
              <div style={widthGuardStyle}>
                <p style={{ margin: "0 0 8px", color: "#5EEAD4", fontSize: 11, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>Histórico</p>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <h3 style={{ margin: 0, color: "#FFF", fontSize: 24, fontWeight: 950 }}>{selectedVisitor.name}</h3>
                  <button
                    type="button"
                    onClick={() => openEditVisitorModal(selectedVisitor)}
                    disabled={!canManage}
                    style={{ ...buttonReset, display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 9px", border: "1px solid rgba(148,163,184,0.3)", background: "rgba(255,255,255,0.04)", color: "#CBD5E1", fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.4, opacity: canManage ? 1 : 0.6 }}
                  >
                    <Pencil size={11} /> Editar
                  </button>
                </div>
                <p style={{ margin: selectedVisitor.householdMembers?.length > 0 ? "0 0 6px" : "0 0 14px", color: "#B6C5D8", fontSize: 13 }}>{selectedVisitor.whatsapp} {selectedVisitor.neighborhood ? `• ${selectedVisitor.neighborhood}` : ""}</p>
                {selectedVisitor.householdMembers?.length > 0 && (
                  <p style={{ margin: "0 0 14px", color: "#7DD3FC", fontSize: 12, fontWeight: 800 }}>
                    {selectedVisitor.isHouseholdContact ? "Responsável do grupo · " : ""}Veio com:{" "}
                    {selectedVisitor.householdMembers.map((member, index) => (
                      <span key={member.id}>
                        {index > 0 ? ", " : ""}
                        <button onClick={() => setSelectedVisitorId(member.id)} style={{ ...buttonReset, display: "inline", padding: 0, color: "#7DD3FC", fontSize: 12, fontWeight: 800, textDecoration: "underline" }}>{member.name}</button>
                      </span>
                    ))}
                  </p>
                )}
                <div style={{ margin: "0 0 14px" }}>
                  <p style={{ margin: "0 0 6px", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 900, color: "#64748B" }}>
                    Etapa da jornada (automática)
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4, marginBottom: 8 }}>
                    {PIPELINE_STAGES.map((item, index, stages) => {
                      const stageInfo = (selectedVisitor.stageTimeline || []).find((stage) => stage.id === item.id);
                      const isCurrent = selectedVisitor.status === item.id;
                      const isReached = Boolean(stageInfo?.reachedAt);
                      return (
                        <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{
                            padding: "4px 8px",
                            fontSize: 10,
                            fontWeight: 900,
                            border: isCurrent ? "1px solid rgba(94,234,212,0.68)" : "1px solid rgba(148,163,184,0.22)",
                            background: isCurrent ? "rgba(20,184,166,0.22)" : isReached ? "rgba(148,163,184,0.14)" : "transparent",
                            color: isCurrent ? "#99F6E4" : isReached ? "#CBD5E1" : "#64748B"
                          }}>
                            {item.label}
                          </span>
                          {index < stages.length - 1 && <span style={{ color: "#475569", fontSize: 10 }}>→</span>}
                        </div>
                      );
                    })}
                  </div>
                  <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 800, color: "#5EEAD4" }}>
                    Há {selectedVisitor.daysInStage} dia{selectedVisitor.daysInStage === 1 ? "" : "s"} em "{selectedVisitor.stageLabel}"
                  </p>
                  {selectedVisitor.displayStatus && selectedVisitor.displayStatus !== selectedVisitor.status && (
                    <div style={{ margin: "0 0 8px" }}>
                      <p style={{ margin: "0 0 2px", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 900, color: "#FCA5A5" }}>
                        Posição manual no quadro
                      </p>
                      <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: "#FCA5A5", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>
                          ⚠ {selectedVisitor.displayLabel}
                          {selectedVisitor.manual
                            ? " · marcado manualmente, não muda sozinho"
                            : ` · ${selectedVisitor.daysSinceVisit} dia${selectedVisitor.daysSinceVisit === 1 ? "" : "s"} sem presença`}
                        </span>
                        {selectedVisitor.manual && (
                          <button
                            type="button"
                            onClick={() => moveVisitorToStage(selectedVisitor, "AUTO", false)}
                            style={{ ...buttonReset, padding: "3px 9px", border: "1px solid rgba(252,165,165,0.4)", background: "rgba(252,165,165,0.08)", color: "#FCA5A5", fontSize: 10, fontWeight: 900, textTransform: "uppercase" }}
                          >
                            Voltar ao automático
                          </button>
                        )}
                      </p>
                    </div>
                  )}
                  {selectedVisitor.status !== "INTEGRATED" && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <button onClick={() => advanceVisitorStage(selectedVisitor, cascadeHouseholdOnAdvance)} style={{ ...buttonReset, minHeight: 36, padding: "0 12px", border: "1px solid rgba(94,234,212,0.45)", background: "rgba(20,184,166,0.14)", color: "#99F6E4", fontSize: 11, fontWeight: 950 }}>
                        Avançar etapa
                      </button>
                      {selectedVisitor.householdMembers?.length > 0 && (
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#CBD5E1" }}>
                          <input type="checkbox" checked={cascadeHouseholdOnAdvance} onChange={(event) => setCascadeHouseholdOnAdvance(event.target.checked)} />
                          Junto com a família
                        </label>
                      )}
                      <select
                        value=""
                        onChange={(event) => { if (event.target.value) moveVisitorToStage(selectedVisitor, event.target.value, cascadeHouseholdOnAdvance); }}
                        style={{ ...inputStyle, minHeight: 36, width: "auto", padding: "0 8px", fontSize: 11 }}
                      >
                        <option value="">Mover para...</option>
                        {PIPELINE_STAGES.filter((item) => item.id !== selectedVisitor.status).map((item) => (
                          <option key={item.id} value={item.id}>{item.label}</option>
                        ))}
                        {MANUAL_STAGE_OPTIONS.filter((item) => item.id !== selectedVisitor.displayStatus).map((item) => (
                          <option key={item.id} value={item.id}>{item.label} (manual)</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {selectedVisitor.status === "INTEGRATED" && (
                  selectedVisitor.convertedMemberId ? (
                    <p style={{ margin: "0 0 14px", fontSize: 12, fontWeight: 800, color: "#7DD3FC", display: "flex", alignItems: "center", gap: 6 }}>
                      <UserPlus size={13} /> Virou membro{selectedVisitor.convertedMember?.fullName ? `: ${selectedVisitor.convertedMember.fullName}` : ""}
                      {" · "}
                      <Link to="/admin/membros" style={{ color: "#7DD3FC" }}>ver em Membros</Link>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowConvertModal(true)}
                      disabled={!canManage}
                      style={{ ...buttonReset, display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36, padding: "0 12px", marginBottom: 14, border: "1px solid rgba(125,211,252,0.42)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 11, fontWeight: 950, opacity: canManage ? 1 : 0.6 }}
                    >
                      <UserPlus size={13} /> Converter em cadastro de membro
                    </button>
                  )
                )}
                {selectedVisitor.status !== "INTEGRATED" && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 9 }}>
                    {selectedVisitor.status === "NEW" && (
                      <button onClick={() => sendVisitorWhatsapp(WHATSAPP_TEMPLATES.WELCOME(selectedVisitor))} style={quickTemplateButtonStyle}>Boas-vindas</button>
                    )}
                    {selectedVisitor.status === "RETURNED" && (
                      <button onClick={() => sendVisitorWhatsapp(WHATSAPP_TEMPLATES.DISCIPLESHIP_INVITE(selectedVisitor))} style={quickTemplateButtonStyle}>Convite discipulado</button>
                    )}
                    {["AWAY", "INVITED_BACK", "NOT_INTEGRATED"].includes(selectedVisitor.displayStatus) && (
                      <>
                        <button onClick={() => sendVisitorWhatsapp(WHATSAPP_TEMPLATES.EVENT_INVITE(selectedVisitor))} style={quickTemplateButtonStyle}>Convite evento</button>
                        <button onClick={() => sendVisitorWhatsapp(WHATSAPP_TEMPLATES.SERVICE_INVITE(selectedVisitor))} style={quickTemplateButtonStyle}>Convite culto</button>
                      </>
                    )}
                  </div>
                )}
                <textarea value={visitorInteractionForm.note} onChange={(event) => updateVisitorInteractionForm("note", event.target.value)} placeholder="Mensagem, anotação ou contexto do próximo contato" rows={5} style={{ ...inputStyle, height: "auto", paddingTop: 12, resize: "vertical", marginBottom: 9 }} />
                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 9, marginBottom: 9 }}>
                  <input value={visitorInteractionForm.title} onChange={(event) => updateVisitorInteractionForm("title", event.target.value)} placeholder="Título da interação" style={inputStyle} />
                  <input value={visitorInteractionForm.eventName} onChange={(event) => updateVisitorInteractionForm("eventName", event.target.value)} placeholder="Culto/evento" style={inputStyle} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(4, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))", gap: 8 }}>
                  <button onClick={sendVisitorWhatsapp} style={{ ...buttonReset, minHeight: 42, border: "1px solid rgba(34,197,94,0.42)", background: "rgba(34,197,94,0.13)", color: "#BBF7D0", fontSize: 11, fontWeight: 950 }}>WhatsApp</button>
                  <button onClick={() => addVisitorInteraction("INVITE")} style={{ ...buttonReset, minHeight: 42, border: "1px solid rgba(230,177,90,0.42)", background: "rgba(230,177,90,0.12)", color: "#FDE68A", fontSize: 11, fontWeight: 950 }}>Convite</button>
                  <button onClick={() => addVisitorInteraction("ATTENDANCE")} style={{ ...buttonReset, minHeight: 42, border: "1px solid rgba(125,211,252,0.42)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 11, fontWeight: 950 }}>Compareceu</button>
                  <button onClick={() => addVisitorInteraction("NOTE")} style={{ ...buttonReset, minHeight: 42, border: "1px solid rgba(148,163,184,0.28)", background: "rgba(255,255,255,0.05)", color: "#E5E7EB", fontSize: 11, fontWeight: 950 }}>Anotar</button>
                </div>
              </div>
              <div style={{ display: "grid", gap: 9, alignContent: "start", minWidth: 0, maxHeight: isDesktop ? 560 : 420, overflowY: "auto", paddingRight: 10 }}>
                {(selectedVisitor.interactions || []).map((interaction) => {
                  const kindStyle = interactionKindStyle[interaction.type] || { color: "#94A3B8", label: interaction.type };
                  const createdAt = new Date(interaction.createdAt);
                  const createdLabel = `${createdAt.toLocaleDateString("pt-BR")} às ${createdAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
                  return (
                    <div key={interaction.id} style={{ maxWidth: "100%", boxSizing: "border-box", border: "1px solid rgba(148,163,184,0.16)", borderLeft: `3px solid ${kindStyle.color}`, background: "rgba(255,255,255,0.035)", padding: 12 }}>
                      {/* Sem overflow:hidden aqui -- num grid item com altura
                          "auto", overflow != visible zera o tamanho minimo
                          automatico (spec do CSS Grid) e corta a ultima linha
                          de texto que quebrou (a nota de "De X para Y", por
                          exemplo). overflowWrap cuida do estouro horizontal
                          sem precisar disso. */}
                      <p style={{ margin: "0 0 5px", color: "#F8FAFC", fontSize: 13, fontWeight: 900, overflowWrap: "anywhere" }}>{interaction.title}</p>
                      <p style={{ margin: "0 0 6px", color: kindStyle.color, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800, overflowWrap: "anywhere" }}>{kindStyle.label} • {createdLabel}</p>
                      {interaction.note && <p style={{ margin: 0, color: "#B6C5D8", fontSize: 12, lineHeight: 1.45, overflowWrap: "anywhere" }}>{interaction.note}</p>}
                    </div>
                  );
                })}
                {(!selectedVisitor.interactions || selectedVisitor.interactions.length === 0) && <p style={{ margin: 0, color: "#9fb0cc", fontSize: 12 }}>Sem interações registradas ainda.</p>}
              </div>
            </div>
          ) : (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>Selecione ou cadastre um visitante para acompanhar.</p>
          )}
        </div>

        {showVisitorFormModal && (
          <div style={modalBackdropStyle} onClick={closeVisitorFormModal}>
            <div style={{ ...modalCardStyle, position: "relative" }} onClick={(event) => event.stopPropagation()}>
              <button type="button" onClick={closeVisitorFormModal} style={modalCloseButtonStyle} disabled={conectaLoading}>
                <X size={16} />
              </button>
              <p style={{ margin: "0 0 14px", color: "#5EEAD4", fontSize: 11, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>{editingVisitorId ? "Editar visitante" : "Novo visitante"}</p>
              <div style={{ display: "grid", gap: 9 }}>
              <label>
                <span style={fieldLabelStyle}>Nome <span style={{ color: "#FCA5A5" }}>*</span></span>
                <input value={visitorForm.name} onChange={(event) => updateVisitorForm("name", event.target.value)} placeholder="Nome do visitante" style={inputStyle} />
              </label>
              <label>
                <span style={fieldLabelStyle}>WhatsApp <span style={{ color: "#FCA5A5" }}>*</span></span>
                <input value={visitorForm.whatsapp} onChange={(event) => updateVisitorForm("whatsapp", event.target.value)} placeholder="WhatsApp" inputMode="tel" style={inputStyle} />
              </label>
              <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 9 }}>
                <label>
                  <span style={fieldLabelStyle}>E-mail</span>
                  <input value={visitorForm.email} onChange={(event) => updateVisitorForm("email", event.target.value)} placeholder="E-mail opcional" inputMode="email" style={inputStyle} />
                </label>
                <label>
                  <span style={fieldLabelStyle}>Cidade</span>
                  <input value={visitorForm.city} onChange={(event) => updateVisitorForm("city", event.target.value)} placeholder="Cidade" style={inputStyle} />
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 9 }}>
                <label>
                  <span style={fieldLabelStyle}>Bairro</span>
                  <input value={visitorForm.neighborhood} onChange={(event) => updateVisitorForm("neighborhood", event.target.value)} placeholder="Bairro" style={inputStyle} />
                </label>
                <label>
                  <span style={fieldLabelStyle}>Como chegou</span>
                  <input value={visitorForm.source} onChange={(event) => updateVisitorForm("source", event.target.value)} placeholder="Como chegou?" style={inputStyle} />
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isTablet && !editingVisitorId ? "1fr 1fr" : "1fr", gap: 9 }}>
                {!editingVisitorId && (
                  <label>
                    <span style={fieldLabelStyle}>Etapa</span>
                    <select value={visitorForm.status} onChange={(event) => updateVisitorForm("status", event.target.value)} style={inputStyle}>
                      {PIPELINE_STAGES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                    </select>
                  </label>
                )}
                <label>
                  <span style={fieldLabelStyle}>Tags</span>
                  <input value={visitorForm.tags} onChange={(event) => updateVisitorForm("tags", event.target.value)} placeholder="Tags separadas por vírgula" style={inputStyle} />
                </label>
              </div>
              <textarea value={visitorForm.notes} onChange={(event) => updateVisitorForm("notes", event.target.value)} placeholder="Observações importantes" rows={3} style={{ ...inputStyle, height: "auto", paddingTop: 12, resize: "vertical" }} />
              {!editingVisitorId && (
                <>
                  <div style={{ position: "relative" }}>
                    <span style={fieldLabelStyle}>Veio com (visitante, família ou membro da igreja)</span>
                    <input value={companionSearch} onChange={(event) => searchConectaCompanions(event.target.value)} placeholder="Buscar visitante ou membro já cadastrado..." style={inputStyle} />
                    {companionResults.length > 0 && (
                      <div style={{ position: "absolute", zIndex: 5, top: "100%", left: 0, right: 0, marginTop: 4, border: "1px solid rgba(94,234,212,0.34)", background: "#0F172A", maxHeight: 180, overflowY: "auto" }}>
                        {companionResults.map((item) => (
                          <button key={item.type + item.id} onClick={() => addCompanion(item)} style={{ ...buttonReset, width: "100%", textAlign: "left", padding: "9px 11px", color: "#E5E7EB", fontSize: 12, borderBottom: "1px solid rgba(148,163,184,0.14)" }}>
                            <strong>{item.name}</strong>
                            <span style={{ marginLeft: 6, color: "#9fb0cc", fontSize: 11 }}>{item.subtitle}</span>
                            <span style={{ marginLeft: 6, color: item.type === "member" ? "#99F6E4" : "#9fb0cc", fontSize: 10, fontWeight: 800, textTransform: "uppercase" }}>
                              {item.type === "member" ? "Membro" : "Visitante"}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {visitorForm.companions.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {visitorForm.companions.map((item) => (
                        <span key={item.type + item.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 9px", border: "1px solid rgba(94,234,212,0.34)", background: "rgba(20,184,166,0.12)", color: "#99F6E4", fontSize: 11, fontWeight: 800 }}>
                          {item.name}
                          {item.type === "member" && <span style={{ fontSize: 9, fontWeight: 900, opacity: 0.8 }}>(membro)</span>}
                          <button onClick={() => removeCompanion(item)} style={{ ...buttonReset, color: "#99F6E4", fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {visitorForm.companions.some((item) => item.type === "visitor") && (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#CBD5E1" }}>
                      <input type="checkbox" checked={visitorForm.markAsHouseholdContact} onChange={(event) => updateVisitorForm("markAsHouseholdContact", event.target.checked)} />
                      Este é o responsável do grupo (recebe o acompanhamento)
                    </label>
                  )}
                </>
              )}
              <button onClick={saveConectaVisitor} disabled={conectaLoading} style={{ ...buttonReset, minHeight: 46, border: "1px solid rgba(94,234,212,0.45)", background: "rgba(20,184,166,0.16)", color: "#99F6E4", fontSize: 12, fontWeight: 950, opacity: conectaLoading ? 0.7 : 1 }}>
                {conectaLoading ? "Salvando..." : editingVisitorId ? "Salvar alterações" : "Salvar visitante"}
              </button>
              {conectaFeedback && <p style={{ margin: 0, color: conectaFeedback.includes("Não") || conectaFeedback.includes("Informe") ? "#FCA5A5" : "#A7F3D0", fontSize: 12 }}>{conectaFeedback}</p>}
              </div>
            </div>
          </div>
        )}
        {showConvertModal && selectedVisitor && (
          <div style={modalBackdropStyle} onClick={() => !convertingVisitorId && setShowConvertModal(false)}>
            <div style={{ ...modalCardStyle, maxWidth: 440, position: "relative" }} onClick={(event) => event.stopPropagation()}>
              <button type="button" onClick={() => setShowConvertModal(false)} style={modalCloseButtonStyle} disabled={Boolean(convertingVisitorId)}>
                <X size={16} />
              </button>
              <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Converter em membro</p>
              <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{selectedVisitor.name}</h2>
              <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
                Cria um cadastro de membro pra {selectedVisitor.name} com nome, WhatsApp, cidade e bairro já preenchidos, mantendo o cadastro de visitante
                (histórico de interações e jornada no Conecta continuam existindo, só passam a mostrar o vínculo com o membro). Use quando a integração
                já estiver confirmada de verdade.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => setShowConvertModal(false)} disabled={Boolean(convertingVisitorId)} style={{ ...buttonReset, minHeight: 38, padding: "0 14px", border: "1px solid rgba(148,163,184,0.28)", background: "rgba(255,255,255,0.03)", color: "#c7ced9", fontSize: 12, fontWeight: 800 }}>
                  Cancelar
                </button>
                <button type="button" onClick={confirmConvertVisitor} disabled={Boolean(convertingVisitorId)} style={{ ...buttonReset, minHeight: 38, padding: "0 14px", border: "1px solid rgba(125,211,252,0.42)", background: "rgba(14,165,233,0.16)", color: "#BAE6FD", fontSize: 12, fontWeight: 950, opacity: convertingVisitorId ? 0.7 : 1 }}>
                  {convertingVisitorId ? "Convertendo..." : "Converter em membro"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
